import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ArrowLeft } from 'lucide-react';
import { isHomamSubdomain, getHomamPath, getMainSiteExternalUrl } from '../utils/domain';
import { useHomam } from '../context/HomamContext';
import { getDynamicWhatsAppNumber, getDynamicFormattedWhatsApp } from '../data/homamData';

export function HomamSubNavbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { whatsappNumber, formattedWhatsAppNumber } = useHomam();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const isSub = isHomamSubdomain();
  const homePath = isSub ? '/' : '/nakshatra-homam';
  const isHomePage = pathname === homePath || pathname === `${homePath}/` || (!isSub && pathname === '/');
  const isSubPage = !isHomePage;

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(homePath);
    }
  };

  const handleSectionClick = (e, sectionId) => {
    e.preventDefault();
    if (pathname === homePath || pathname === `${homePath}/`) {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate(`${homePath}#${sectionId}`);
    }
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { name: 'Home', path: getHomamPath('/') },
    { name: "Today's Day", path: getHomamPath('/today') },
    { name: '27 Nakshatras', path: `${getHomamPath('/')}#nakshatras`, sectionId: 'nakshatras' },
    { name: 'Gallery', path: getHomamPath('/gallery') },
    { name: 'Contact', path: getHomamPath('/contact') },
  ];

  const mainSiteHomeUrl = getMainSiteExternalUrl('/');

  return (
    <>
      <div className="homam-sticky-header">
        {/* 1. Golden Topbar */}
        <div className="homam-topbar">
          <div className="homam-topbar-container">
            <div className="homam-topbar-left">
              <span className="homam-topbar-mandir-title">Sri Gnana Sai Baba Mandir</span>
              <span className="homam-topbar-divider">•</span>
              <span className="homam-topbar-subtext">27 Nakshatra Maha Homam</span>
            </div>
            <div className="homam-topbar-right">
              <a href="tel:+916381720140" className="homam-topbar-phone">
                <span>📞 +91 63817 20140</span>
              </a>
              <span className="homam-topbar-chant">ஓம் ஸ்ரீ ஞான சாய் பாபா போற்றி</span>
            </div>
          </div>
        </div>

        {/* 2. Main Sub-Navbar */}
        <header className="homam-navbar">
          <div className="homam-navbar-container">
            {/* Mobile Back Button (Only on mobile subpages to return to previous page) */}
            {isSubPage && (
              <button
                type="button"
                className="homam-mobile-nav-back-btn"
                onClick={handleBack}
                aria-label="Back to previous page"
                title="Back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}

            {/* Logo & Brand Name */}
            <Link to={getHomamPath('/')} className="homam-nav-brand">
              <div className="homam-logo-frame">
                <img
                  src="/images/sailogo.png"
                  alt="Sri Gnana Sai Baba Mandir Logo"
                  onError={(e) => { e.target.src = '/logo192.png'; }}
                />
              </div>
              <div className="homam-nav-titles">
                <span className="homam-nav-main-title">SRI GNANA SAI BABA MANDIR</span>
                <div className="homam-nav-sub-title">
                  <span className="homam-nav-star-text">27 Nakshatra Homam</span>
                  <span className="tamil-title">(ஸ்ரீ 27 நட்சத்திர மகா ஹோமம்)</span>
                </div>
              </div>
            </Link>

            {/* Desktop Center Links */}
            <nav className="homam-center-links">
              {navLinks.map((l) => (
                l.sectionId ? (
                  <a
                    key={l.name}
                    href={l.path}
                    onClick={(e) => handleSectionClick(e, l.sectionId)}
                    className={`homam-nav-btn ${pathname === homePath && location.hash === `#${l.sectionId}` ? 'active' : ''}`}
                  >
                    {l.name}
                  </a>
                ) : (
                  <Link
                    key={l.name}
                    to={l.path}
                    className={`homam-nav-btn ${pathname === l.path ? 'active' : ''}`}
                  >
                    {l.name}
                  </Link>
                )
              ))}
            </nav>

            {/* Desktop Right Actions */}
            <div className="homam-nav-actions">
              {isSub ? (
                <a
                  href={mainSiteHomeUrl}
                  className="btn-contact-guruji"
                  title="Return to Sri Gnana Sai Baba Mandir Main Site"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <span>Back to Sri Gnana Sai Baba Mandir</span>
                </a>
              ) : (
                <Link
                  to="/"
                  className="btn-contact-guruji"
                  title="Return to Sri Gnana Sai Baba Mandir"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <span>Back to Sri Gnana Sai Baba Mandir</span>
                </Link>
              )}
            </div>

            {/* Mobile Menu Toggle Button */}
            <button
              type="button"
              className="homam-mobile-toggle-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Dropdown Menu Drawer */}
          {mobileMenuOpen && (
            <div className="homam-mobile-drawer">
              <div className="homam-mobile-drawer-chant">
                <span>ஓம் ஸ்ரீ ஞான சாய் பாபா போற்றி 🙏</span>
              </div>
              <div className="homam-mobile-nav-links">
                {navLinks.map((l) => (
                  l.sectionId ? (
                    <a
                      key={l.name}
                      href={l.path}
                      onClick={(e) => handleSectionClick(e, l.sectionId)}
                      className="homam-mobile-nav-item"
                    >
                      <span>{l.name}</span>
                    </a>
                  ) : (
                    <Link
                      key={l.name}
                      to={l.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="homam-mobile-nav-item"
                    >
                      <span>{l.name}</span>
                    </Link>
                  )
                ))}
              </div>
              <div className="homam-mobile-drawer-footer">
                <a href="tel:+916381720140" className="mobile-drawer-contact-btn call">
                  <span>📞 Call Helpline: +91 63817 20140</span>
                </a>
                <a
                  href={`https://wa.me/${(whatsappNumber || getDynamicWhatsAppNumber()).replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mobile-drawer-contact-btn whatsapp"
                >
                  <span>💬 WhatsApp Mandir: {formattedWhatsAppNumber || getDynamicFormattedWhatsApp()}</span>
                </a>
                {isSub ? (
                  <a
                    href={mainSiteHomeUrl}
                    onClick={() => setMobileMenuOpen(false)}
                    className="mobile-drawer-back-btn"
                  >
                    ← Back to Sri Gnana Sai Baba Mandir
                  </a>
                ) : (
                  <Link
                    to="/"
                    onClick={() => setMobileMenuOpen(false)}
                    className="mobile-drawer-back-btn"
                  >
                    ← Back to Sri Gnana Sai Baba Mandir
                  </Link>
                )}
              </div>
            </div>
          )}
        </header>
      </div>
    </>
  );
}

export default HomamSubNavbar;

